import { useState, type ReactNode } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn, Popover, PopoverContent, PopoverTrigger } from "@nexus/ui";

export interface FilterSelectOption {
  value: string;
  label: string;
}

interface FilterSelectChipProps {
  icon: ReactNode;
  label: string;
  active: boolean;
  value: string;
  options: FilterSelectOption[];
  onValueChange: (value: string) => void;
}

/** Visible on popover in light and dark — dark `--muted` matches `--popover`. */
const OPTION_HOVER_CLASS =
  "hover:bg-[color-mix(in_srgb,var(--foreground)_8%,var(--popover))]";

/**
 * Pill-shaped single-select filter. Opens a themed Popover (same shell as the
 * Organizations color chip) instead of a native OS `<select>`.
 */
export function FilterSelectChip({
  icon,
  label,
  active,
  value,
  options,
  onValueChange,
}: FilterSelectChipProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex cursor-pointer select-none items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
            active
              ? "border-primary/40 bg-primary/10 text-primary"
              : "border-border bg-transparent text-muted-foreground hover:border-foreground/30 hover:text-foreground",
          )}
        >
          {icon}
          <span className="max-w-[120px] truncate">{label}</span>
          <ChevronDown className="h-2.5 w-2.5 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-48 max-h-64 overflow-y-auto p-1.5"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {options.map((opt) => {
          const selected = opt.value === value;
          return (
            <button
              key={opt.value || "__all__"}
              type="button"
              role="option"
              aria-selected={selected}
              className={cn(
                "flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs",
                selected
                  ? "bg-primary/10 text-primary dark:bg-primary/20"
                  : cn("text-foreground", OPTION_HOVER_CLASS),
              )}
              onClick={() => {
                onValueChange(opt.value);
                setOpen(false);
              }}
            >
              <span className="flex h-3 w-3 shrink-0 items-center justify-center">
                {selected ? <Check className="h-3 w-3" /> : null}
              </span>
              <span className="min-w-0 truncate">{opt.label}</span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
